// An Audiotool project, published as a seed — without Live.
//
// The other half of "Open in Audiotool": a producer edits the project over
// there and puts the result back in the Crate, as a new version of the seed it
// came from, a remix of someone else's, or a new root. Runs wherever the SDK
// does — the web app's tab and the extension's Node alike — so everything
// environment-specific comes in through a transport:
//
//   • captureProject reads the document and cuts each clip's own audio out of
//     the samples it plays;
//   • shared/upload/publish-seed.ts does the rest — hash, master, MP3
//     previews, and the same three calls a Live upload makes (manifest →
//     presigned PUTs → complete), over whatever the transport reaches it by.
//     The web app's folder upload publishes through the same steps;
//   • finally the project is stamped with the new seed (./link.ts), so the
//     seed's page finds it next time and the next import chains off this one.
//
// Bytes are content-addressed by full sha-256, so a clip whose audio is already
// in the blob pool is never re-sent — including, usually, most of a project
// that came FROM Seedbed in the first place.

import type { AuthenticatedClient } from "@audiotool/nexus";

import {
  completeSeedUpload,
  openSeedUpload,
  prepareSeed,
  putUploadJob,
  type CompletedFile,
  type SeedbedTransport,
} from "../../../shared/upload/publish-seed.js";
import { encodePreviews } from "../upload/preview-encode.js";
import { captureProject, type Capture } from "./capture.js";
import { linkQuietly } from "./link.js";
import type { PushReporter } from "./progress.js";
import type { RenderApi } from "./render.js";

// The transport lives with the publish steps now
// (shared/upload/publish-seed.ts); re-exported for this module's callers.
export { type SeedbedTransport } from "../../../shared/upload/publish-seed.js";

export type ProjectUploadClient = Pick<AuthenticatedClient, "open" | "samples"> & Partial<Pick<AuthenticatedClient, "projects">>;

export interface ProjectUploadRequest {
  /** The Audiotool project to capture. */
  project: { name: string; displayName: string };
  title: string;
  /** The seed this is a new version (or remix) of, or null for a new root. */
  parent: { seedId: string; rootSeedId: string } | null;
  note: string | null;
  /** Where seed links written onto the project point; the web app's own origin, or its default. */
  linkOrigin?: string;
}

export interface ProjectUploadOutcome {
  seedId: string;
  title: string;
  tempo: number;
  audioTracks: number;
  midiTracks: number;
  clips: number;
  /** Objects the pool already had — not re-sent. */
  reused: number;
  /** Whether the project now points at the new seed. */
  linked: boolean;
  /** Everything the producer should know: what didn't come across. */
  notes: string[];
}

export async function uploadProjectToSeedbed(
  client: ProjectUploadClient,
  transport: SeedbedTransport,
  req: ProjectUploadRequest,
  opts: { report?: PushReporter; signal?: AbortSignal; renders?: RenderApi } = {},
): Promise<ProjectUploadOutcome> {
  const say = opts.report ?? ((): void => {});

  const capture = await captureProject(client, req.project, opts);
  if (capture.snapshot.audioTracks.length === 0 && (capture.snapshot.midiTracks ?? []).length === 0) {
    throw new Error(`"${req.project.displayName}" has no tracks to upload.`);
  }

  const prepared = await prepareSeed(
    {
      meta: {
        title: req.title,
        bpm: capture.tempo,
        // Audiotool holds no key, and a guess would be worse than a blank.
        key: "",
        parentSeedId: req.parent?.seedId ?? null,
        note: req.note,
        origin: "audiotool",
      },
      snapshot: capture.snapshot,
      clips: capture.clips,
      midiClips: capture.midiClips,
      // Audiotool's own render when it gave one; otherwise a leveled sum.
      master: capture.master,
    },
    encodePreviews,
    (stage) => say({ kind: "stage", stage }),
  );

  // ---- Send only what the pool doesn't already hold ------------------------
  const session = await openSeedUpload(transport, prepared);
  const completed: CompletedFile[] = [];
  let sent = 0;
  say({ kind: "stage", stage: "publishing", done: 0, total: session.jobs.length });
  for (const job of session.jobs) {
    if (opts.signal?.aborted) throw new Error("Upload cancelled.");
    say({ kind: "sample", id: job.plan.key, displayName: job.label, state: "uploading", bytes: job.bytes.byteLength });
    completed.push(await putUploadJob(transport, job));
    say({ kind: "sample", id: job.plan.key, displayName: job.label, state: "uploaded", bytes: job.bytes.byteLength });
    say({ kind: "stage", stage: "publishing", done: ++sent, total: session.jobs.length });
  }

  // ---- Seal it: the server verifies every object before the seed goes live --
  const done = await completeSeedUpload(transport, session, completed);

  // ---- Point the project at the seed it just became -------------------------
  // The seed is published either way; a project that can't be stamped only
  // means the seed's page won't find it on its own.
  const notes = describeCapture(capture);
  let linked = false;
  if (client.projects) {
    const rootSeedId = req.parent?.rootSeedId ?? done.seedId;
    const failed = await linkQuietly({ projects: client.projects }, req.project.name, { rootSeedId, seedId: done.seedId }, req.linkOrigin);
    if (failed) notes.push(failed);
    else linked = true;
  }
  say({ kind: "stage", stage: "done" });

  return {
    seedId: done.seedId,
    title: prepared.create.title,
    tempo: prepared.create.bpm,
    audioTracks: capture.snapshot.audioTracks.length,
    midiTracks: (capture.snapshot.midiTracks ?? []).length,
    clips: prepared.placements.length,
    reused: session.reused,
    linked,
    notes,
  };
}

/** Everything the producer should know about what didn't come across. */
export function describeCapture(capture: Capture): string[] {
  const midiTracks = (capture.snapshot.midiTracks ?? []).length;
  const bounced = new Set(capture.midiClips.map((c) => c.trackIndex)).size;
  return [
    ...capture.warnings.map((w) => w.detail),
    ...(capture.missing.length > 0
      ? [`Audiotool wouldn't give up ${capture.missing.length} sample(s) (${capture.missing.slice(0, 3).join(", ")}) — those clips arrive silent.`]
      : []),
    ...(midiTracks > 0 && bounced < midiTracks
      ? [bounced === 0
          ? "MIDI notes came across; the Audiotool instruments playing them did not."
          : `MIDI notes came across with their sound for ${bounced} of ${midiTracks} track(s); the rest arrive without an instrument.`]
      : []),
    ...(capture.rendered && !capture.rendered.master
      ? ["Audiotool couldn't render the mix, so the master is Seedbed's own sum of the clips — without instruments or effects."]
      : []),
    ...(capture.rendered?.errors?.length ? [`Audiotool said: ${capture.rendered.errors.slice(0, 2).join(" / ")}`] : []),
    ...(capture.rendered && capture.rendered.failed > 0
      ? [`Audiotool couldn't render ${capture.rendered.failed} track(s) on their own; those fell back to their samples.`]
      : []),
  ];
}

// ---------------------------------------------------------------------------
// Bytes
// ---------------------------------------------------------------------------

/** Full sha-256 hex — the blob pool's address, same as a Live upload sends. */
export { sha256Hex } from "../../../shared/hash/sha256.js";
